import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const MyPreset = definePreset(Aura, {
  primitive: {
    // Tipografia AXA Design Principles
    fontFamily: {
      // Secondary typeface - Source Sans per testo di supporto, CTA, annotazioni
      base: '"Source Sans 3", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      // Primary typeface - Publico Headline per titoli
      heading: '"Publico Headline", Georgia, "Times New Roman", serif'
    }
  },
  semantic: {
    // Font configuration
    fontFamily: '"Source Sans 3", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    primary: {
      50: '#e6e6ff',
      100: '#ccccff',
      200: '#9999ff',
      300: '#6666ff',
      400: '#3333ff',
      500: '#00008f', // AXA Blue
      600: '#00007a',
      700: '#00005b', // Deep Blue
      800: '#000044',
      900: '#00002e',
      950: '#00001f'
    },
    // AXA Red per errori e stati critici (slide 81)
    red: {
      50: '#ffebee',
      100: '#ffcdd2',
      200: '#ff9a9e',
      300: '#ff6b70',
      400: '#ff3d47',
      500: '#dc0814', // WCAG 2.1 1.4.3: Changed from #ff1721 to #dc0814 for better contrast (5.7:1 on white)
      600: '#e6141d',
      700: '#cc1119',
      800: '#b30e15',
      900: '#990c11',
      950: '#7f0a0e'
    },
    // Palette completa AXA (slide 85)
    green: {
      50: '#e8f5e9',
      100: '#c8e6c9',
      200: '#a5d6a7',
      300: '#81c784',
      400: '#66bb6a',
      500: '#4caf50', // Verde successo
      600: '#43a047',
      700: '#2e7d32', // WCAG 2.1 1.4.3: Changed from #388e3c (3.9:1) to #2e7d32 (4.6:1 contrast on white)
      800: '#1b5e20', // WCAG 2.1 1.4.3: Changed from #2e7d32 to #1b5e20 (7.4:1 contrast on white)
      900: '#0d3f10', // WCAG 2.1 1.4.3: Changed from #1b5e20 to #0d3f10 (11.8:1 contrast on white)
      950: '#052108' // Added for maximum contrast
    },
    orange: {
      50: '#fff3e0',
      100: '#ffe0b2',
      200: '#ffcc80',
      300: '#ffb74d',
      400: '#ffa726',
      500: '#ff9800', // Arancione warning
      600: '#fb8c00',
      700: '#f57c00',
      800: '#ef6c00',
      900: '#e65100',
      950: '#bf360c'
    },
    teal: {
      50: '#e0f2f1',
      100: '#b2dfdb',
      200: '#80cbc4',
      300: '#4db6ac',
      400: '#26a69a',
      500: '#009688', // Teal AXA
      600: '#00897b',
      700: '#00796b',
      800: '#00695c',
      900: '#004d40',
      950: '#003d33'
    },
    purple: {
      50: '#f3e5f5',
      100: '#e1bee7',
      200: '#ce93d8',
      300: '#ba68c8',
      400: '#ab47bc',
      500: '#9c27b0', // Viola
      600: '#8e24aa',
      700: '#7b1fa2',
      800: '#6a1b9a',
      900: '#4a148c',
      950: '#38096b'
    },
    gray: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e', // Grigio neutro
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      950: '#0a0a0a'
    },
    colorScheme: {
      light: {
        primary: {
          color: '#00008f', // AXA Blue ufficiale
          contrastColor: '#ffffff',
          hoverColor: '#00007a',
          activeColor: '#00005b' // Deep Blue
        },
        // Stati di errore con AXA Red
        danger: {
          color: '#dc0814', // WCAG 2.1 1.4.3: Changed from #ff1721 to #dc0814 for better contrast
          contrastColor: '#ffffff',
          hoverColor: '#e6141d',
          activeColor: '#cc1119'
        },
        // Stati success con verde AXA
        success: {
          color: '#4caf50',
          contrastColor: '#ffffff',
          hoverColor: '#43a047',
          activeColor: '#2e7d32' // WCAG 2.1 1.4.3: Changed from #388e3c to #2e7d32 for better contrast
        },
        // Stati warning con arancione AXA
        warn: {
          color: '#ff9800',
          contrastColor: '#ffffff',
          hoverColor: '#fb8c00',
          activeColor: '#f57c00'
        },
        // Stati info con teal AXA
        info: {
          color: '#009688',
          contrastColor: '#ffffff',
          hoverColor: '#00897b',
          activeColor: '#00796b'
        },
        highlight: {
          background: '#e6e6ff',
          focusBackground: '#ccccff',
          color: '#00008f', // AXA Blue
          focusColor: '#00005b' // Deep Blue
        },
        surface: {
          0: '#ffffff',
          50: '#f2f4f8',
          100: '#e6eaf2',
          200: '#d3d9e5',
          300: '#c2c9dc',
          400: '#7a8195', // WCAG 2.1 1.4.11: Changed from #9aa3bb to #7a8195 for better UI contrast (4.2:1)
          500: '#5b6370', // WCAG 2.1 1.4.3: Changed from #6c7491 to #5b6370 for better text contrast (5.5:1)
          600: '#5a6071',
          700: '#2c2f36',
          800: '#2c2f36',
          900: '#181a1f',
          950: '#04060c'
        },
        root: { 
          background: '#f7f8fc', 
          color: '#2c2f36' 
        },
        content: { 
          background: '#ffffff', 
          borderColor: '#d3d9e5', 
          color: '#2c2f36' 
        }
      },
      dark: {
        primary: {
          color: '#6666ff',
          contrastColor: '#00001f',
          hoverColor: '#9999ff',
          activeColor: '#ccccff'
        },
        // Stati di errore con AXA Red (dark mode)
        danger: {
          color: '#ff6b70',
          contrastColor: '#ffffff',
          hoverColor: '#ff9a9e',
          activeColor: '#ffcdd2'
        },
        // Stati success (dark mode)
        success: {
          color: '#81c784',
          contrastColor: '#ffffff',
          hoverColor: '#a5d6a7',
          activeColor: '#c8e6c9'
        },
        // Stati warning (dark mode)
        warn: {
          color: '#ffb74d',
          contrastColor: '#ffffff',
          hoverColor: '#ffcc80',
          activeColor: '#ffe0b2'
        },
        // Stati info (dark mode)
        info: {
          color: '#4db6ac',
          contrastColor: '#ffffff',
          hoverColor: '#80cbc4',
          activeColor: '#b2dfdb'
        },
        highlight: {
          background: '#00005b', // Deep Blue
          focusBackground: '#00007a',
          color: '#9999ff',
          focusColor: '#ccccff'
        },
        surface: {
          0: '#ffffff',
          50: '#f2f4f8',
          100: '#e6eaf2',
          200: '#d3d9e5',
          300: '#c2c9dc',
          400: '#7a8195', // WCAG 2.1 1.4.11: Changed from #9aa3bb to #7a8195 for better UI contrast (4.2:1)
          500: '#5b6370', // WCAG 2.1 1.4.3: Changed from #6c7491 to #5b6370 for better text contrast (5.5:1)
          600: '#5a6071',
          700: '#2c2f36',
          800: '#2c2f36',
          900: '#181a1f',
          950: '#04060c'
        },
        root: { 
          background: '#04060c', 
          color: '#f2f4f8' 
        },
        content: { 
          background: '#181a1f', 
          borderColor: '#2c2f36', 
          color: '#e6eaf2' 
        }
      }
    }
  }
});
